import { Component, ElementRef, OnInit, ViewChild, inject, OnDestroy, PLATFORM_ID, AfterViewInit } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { lastValueFrom } from 'rxjs';
import { ThinkingOrbComponent } from '../thinking-orb/thinking-orb.component';

@Component({
  selector: 'app-viewer',
  standalone: true,
  imports: [CommonModule, ThinkingOrbComponent],
  template: `
    <div class="h-screen w-full bg-black relative flex flex-col">
      <!-- Loading -->
      <div *ngIf="loading && !errorState" class="absolute inset-0 flex items-center justify-center z-50 bg-black">
        <app-thinking-orb state="working" [size]="128" class="text-zaxis-accent"></app-thinking-orb>
      </div>

      <!-- Error State -->
      <div *ngIf="errorState" class="absolute inset-0 flex flex-col items-center justify-center z-50 bg-black">
        <div class="bg-black border border-zaxis-danger p-8 max-w-md text-center rounded-none relative">
          <div class="absolute top-0 left-0 w-full h-1 bg-zaxis-danger"></div>
          <i class="ph ph-warning text-5xl text-zaxis-danger mb-4 block"></i>
          <h2 class="text-lg font-mono font-bold tracking-widest text-white mb-2 uppercase">Error al cargar la sesión</h2>
          <p class="text-gray-400 text-[10px] font-mono tracking-widest uppercase mb-6">No se pudo cargar la telemetría de esta sesión o no tienes permisos suficientes.</p>
          <button (click)="goBack()" class="px-6 py-2 bg-zaxis-danger/10 hover:bg-zaxis-danger/20 text-zaxis-danger border border-zaxis-danger rounded-none font-mono text-[10px] font-bold tracking-widest uppercase transition-all">
            Volver al Dashboard
          </button>
        </div>
      </div>

      <!-- Top Bar -->
      <header class="absolute top-0 left-0 w-full p-4 flex justify-between items-center bg-black/60 border-b border-zaxis-border backdrop-blur-md z-10">
        <div>
          <button (click)="goBack()" class="text-gray-400 hover:text-white flex items-center gap-2 transition-colors font-mono uppercase tracking-widest text-[10px] font-bold">
            <i class="ph ph-arrow-left text-lg"></i>
            Volver al Dashboard
          </button>
        </div>
        <div class="text-white text-right flex items-center gap-4">
          <div *ngIf="isLive" class="flex items-center gap-2 px-3 py-1 bg-zaxis-danger/10 text-zaxis-danger rounded-none border border-zaxis-danger">
            <span class="w-2 h-2 rounded-none bg-zaxis-danger animate-pulse"></span>
            <span class="text-[9px] font-bold tracking-widest font-mono uppercase">LIVE</span>
          </div>
          <div class="text-right">
            <h2 class="font-mono font-bold text-sm tracking-widest uppercase text-white">Reproducción de Sesión</h2>
            <p class="text-[9px] text-gray-400 font-mono uppercase tracking-widest">{{ session?.id }}</p>
          </div>
        </div>
      </header>

      <!-- 3D Canvas -->
      <div #rendererContainer class="flex-1 w-full relative"></div>

      <!-- Playback Controls -->
      <div class="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black border border-zaxis-border rounded-none shadow-pixel p-4 w-[600px] max-w-[90vw] flex flex-col gap-4 z-10 animate-panel-in">
        <!-- Progress Bar -->
        <div class="relative w-full h-1 bg-black border-b border-zaxis-border cursor-pointer group" (click)="seek($event)">
          <div class="absolute top-0 left-0 h-full bg-zaxis-accent transition-all duration-100 shadow-[0_0_10px_rgba(59,130,246,0.5)] group-hover:h-2" [style.width.%]="progress"></div>
        </div>
        
        <div class="flex justify-between items-center text-white">
          <span class="text-[10px] font-mono tracking-widest text-zaxis-accent font-bold">{{ formatTime(currentTime) }}</span>
          
          <div class="flex gap-4">
            <button (click)="togglePlay()" class="w-10 h-10 flex items-center justify-center bg-black hover:bg-zaxis-accent hover:text-black rounded-none transition-all border border-zaxis-border hover:border-zaxis-accent hover:shadow-pixel hover:-translate-y-0.5 hover:-translate-x-0.5 text-white">
              <i *ngIf="!playing" class="ph-fill ph-play text-sm"></i>
              <i *ngIf="playing" class="ph-fill ph-pause text-sm"></i>
            </button>
          </div>
          
          <span class="text-[10px] font-mono tracking-widest text-gray-500">{{ formatTime(duration) }}</span>
        </div>
      </div>
    </div>
  `
})
export class ViewerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('rendererContainer', { static: true }) rendererContainer!: ElementRef;
  
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private platformId = inject(PLATFORM_ID);
  
  session: any;
  errorState = false;
  loading = true;
  telemetryFrames: any[] = [];
  
  // Three.js
  scene!: THREE.Scene;
  camera!: THREE.PerspectiveCamera;
  renderer!: THREE.WebGLRenderer;
  controls!: OrbitControls;
  
  // Entities
  headMesh!: THREE.Mesh;
  leftHandMesh!: THREE.Mesh;
  rightHandMesh!: THREE.Mesh;
  
  // Playback state
  playing = false;
  currentTime = 0;
  duration = 0;
  progress = 0;
  currentFrameIdx = 0;
  animationFrameId = 0;
  lastTimestamp = 0;
  
  // Live polling
  isLive = false;
  pollingInterval: any;

  async ngOnInit() {
    const sessionId = this.route.snapshot.paramMap.get('id');
    if (isPlatformBrowser(this.platformId) && history.state?.session) {
      this.setupSession(history.state.session);
      this.loading = false;
    } else if (sessionId) {
      if (isPlatformBrowser(this.platformId)) {
        await this.fetchSession(sessionId);
      }
    } else {
      this.router.navigate(['/dashboard']);
      return;
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.initThreeJS();
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
      if (this.pollingInterval) clearInterval(this.pollingInterval);
      if (this.renderer && this.rendererContainer) {
        this.renderer.dispose();
        if (this.rendererContainer.nativeElement && this.renderer.domElement) {
          try {
            this.rendererContainer.nativeElement.removeChild(this.renderer.domElement);
          } catch (e) {}
        }
      }
    }
  }

  setupSession(sessionData: any) {
    this.session = sessionData;
    if (this.session.telemetry && Array.isArray(this.session.telemetry)) {
      this.telemetryFrames = [...this.session.telemetry].sort((a: any, b: any) => a.timestampOffsetMs - b.timestampOffsetMs);
      
      let lastHead: any = null;
      let lastLeftHand: any = null;
      let lastRightHand: any = null;
      
      for (const frame of this.telemetryFrames) {
        if (frame.head) lastHead = { ...frame.head };
        else if (lastHead) frame.head = { ...lastHead };

        if (frame.leftHand) lastLeftHand = { ...frame.leftHand };
        else if (lastLeftHand) frame.leftHand = { ...lastLeftHand };

        if (frame.rightHand) lastRightHand = { ...frame.rightHand };
        else if (lastRightHand) frame.rightHand = { ...lastRightHand };
      }

      if (this.telemetryFrames.length > 0) {
        const first = this.telemetryFrames[0].timestampOffsetMs;
        const last = this.telemetryFrames[this.telemetryFrames.length - 1].timestampOffsetMs;
        this.duration = (last - first) / 1000;
        
        if (this.isLive && (this.duration - this.currentTime) < 12) {
            this.currentTime = this.duration; 
        }
      }
    }
    
    if (isPlatformBrowser(this.platformId)) {
      this.checkLivenessAndPoll();
      if (this.telemetryFrames.length > 0 && this.scene) {
        setTimeout(() => {
          this.onWindowResize();
          this.applyFrame(0);
        }, 0);
      }
    }
  }

  async fetchSession(id: string) {
    try {
      const headers = new HttpHeaders().set('Authorization', `Bearer ${this.auth.getToken()}`);
      const session = await lastValueFrom(this.http.get<any>(`/api/bff/sessions/${id}`, { headers }));
      this.setupSession(session);
      this.loading = false;
    } catch (err) {
      console.error('Failed to fetch session', err);
      this.errorState = true;
      this.loading = false;
    }
  }

  checkLivenessAndPoll() {
    if (!this.session) return;
    
    const receivedTime = new Date(this.session.backend_received_at).getTime();
    const ageMs = Date.now() - receivedTime;
    
    // Si la sesión no es un crash, y recibimos datos hace menos de 35 segundos, la consideramos "VIVA"
    this.isLive = !this.session.hasCrash && ageMs < 35000;
    
    if (this.isLive && !this.pollingInterval) {
      this.playing = true; // Auto-play if live
      this.lastTimestamp = performance.now();
      
      this.pollingInterval = setInterval(async () => {
        await this.fetchSession(this.session.id);
        
        // Re-check liveness after fetch
        const newAgeMs = Date.now() - new Date(this.session.backend_received_at).getTime();
        if (this.session.hasCrash || newAgeMs >= 35000) {
          this.isLive = false;
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
        }
      }, 10000);
    }
  }

  goBack() {
    this.router.navigate(['/dashboard']);
  }

  initThreeJS() {
    const container = this.rendererContainer.nativeElement;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0a0a);
    this.scene.fog = new THREE.Fog(0x0a0a0a, 10, 50);

    // Grid Helper
    const grid = new THREE.GridHelper(20, 20, 0x3b82f6, 0x1e293b);
    this.scene.add(grid);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    this.scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 5);
    this.scene.add(directionalLight);

    // Camera
    this.camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 100);
    this.camera.position.set(0, 2, 4);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(this.renderer.domElement);

    // Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.target.set(0, 1.5, 0); // Target average head height
    this.controls.update();

    window.addEventListener('resize', this.onWindowResize.bind(this));
    
    // Create the meshes BEFORE applying the first frame
    this.createDummyModels();

    // Set initial frame if available
    if (this.telemetryFrames.length > 0) {
      this.applyFrame(0);
    }

    this.animate();
  }

  createDummyModels() {
    // Para prototipo rapido usamos primitivas estilizadas, podemos cambiar a GLTF luego
    
    // Gafas (Head)
    const headGeom = new THREE.BoxGeometry(0.2, 0.1, 0.1);
    const headMat = new THREE.MeshStandardMaterial({ color: 0x3b82f6, roughness: 0.2, metalness: 0.8 });
    this.headMesh = new THREE.Mesh(headGeom, headMat);
    this.scene.add(this.headMesh);

    // Left Hand
    const handGeom = new THREE.BoxGeometry(0.05, 0.1, 0.15);
    const handMat = new THREE.MeshStandardMaterial({ color: 0x60a5fa, roughness: 0.5 });
    this.leftHandMesh = new THREE.Mesh(handGeom, handMat);
    this.scene.add(this.leftHandMesh);

    // Right Hand
    this.rightHandMesh = new THREE.Mesh(handGeom, handMat);
    this.scene.add(this.rightHandMesh);
    
    // Optional: add a ray indicating gaze direction
    const gazeMat = new THREE.LineBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0.5 });
    const gazeGeom = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0,0,0), new THREE.Vector3(0,0,5)]);
    const gazeLine = new THREE.Line(gazeGeom, gazeMat);
    this.headMesh.add(gazeLine); // attach to head
  }

  onWindowResize() {
    if (!this.camera || !this.renderer) return;
    const container = this.rendererContainer.nativeElement;
    this.camera.aspect = container.clientWidth / container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(container.clientWidth, container.clientHeight);
  }

  animate() {
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
    
    const now = performance.now();
    if (this.playing) {
      const dt = (now - this.lastTimestamp) / 1000;
      this.currentTime += dt;
      
      if (this.currentTime >= this.duration) {
        this.currentTime = this.duration;
        this.playing = false;
      }
      
      this.updateProgress();
      this.findAndApplyFrame();
    }
    this.lastTimestamp = now;

    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  togglePlay() {
    this.playing = !this.playing;
    if (this.playing && this.currentTime >= this.duration) {
      this.currentTime = 0;
    }
    this.lastTimestamp = performance.now();
  }

  seek(event: MouseEvent) {
    const bar = event.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    
    this.currentTime = percent * this.duration;
    this.updateProgress();
    this.findAndApplyFrame();
  }

  updateProgress() {
    this.progress = (this.currentTime / this.duration) * 100;
  }

  findAndApplyFrame() {
    if (this.telemetryFrames.length === 0) return;
    
    const firstTs = this.telemetryFrames[0].timestampOffsetMs;
    const targetTs = firstTs + (this.currentTime * 1000);
    
    // Simple linear search for now, could use binary search for perf
    let bestIdx = 0;
    for (let i = 0; i < this.telemetryFrames.length; i++) {
      if (this.telemetryFrames[i].timestampOffsetMs <= targetTs) {
        bestIdx = i;
      } else {
        break;
      }
    }
    
    if (bestIdx !== this.currentFrameIdx) {
      this.applyFrame(bestIdx);
      this.currentFrameIdx = bestIdx;
    }
  }

  applyFrame(index: number) {
    const frame = this.telemetryFrames[index];
    if (!frame) return;

    if (frame.head) {
      this.headMesh.position.set(frame.head.x, frame.head.y, -frame.head.z);
      this.headMesh.quaternion.set(-frame.head.qx, -frame.head.qy, frame.head.qz, frame.head.qw);
      if (frame.head.sx !== undefined) this.headMesh.scale.set(frame.head.sx, frame.head.sy, frame.head.sz);
    }
    if (frame.leftHand) {
      this.leftHandMesh.position.set(frame.leftHand.x, frame.leftHand.y, -frame.leftHand.z);
      this.leftHandMesh.quaternion.set(-frame.leftHand.qx, -frame.leftHand.qy, frame.leftHand.qz, frame.leftHand.qw);
      if (frame.leftHand.sx !== undefined) this.leftHandMesh.scale.set(frame.leftHand.sx, frame.leftHand.sy, frame.leftHand.sz);
    }
    if (frame.rightHand) {
      this.rightHandMesh.position.set(frame.rightHand.x, frame.rightHand.y, -frame.rightHand.z);
      this.rightHandMesh.quaternion.set(-frame.rightHand.qx, -frame.rightHand.qy, frame.rightHand.qz, frame.rightHand.qw);
      if (frame.rightHand.sx !== undefined) this.rightHandMesh.scale.set(frame.rightHand.sx, frame.rightHand.sy, frame.rightHand.sz);
    }
  }

  formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m + ":" + s.toString().padStart(2, '0');
  }
}
